import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { ProductTypeBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { ResponsiveOverlay, useResponsiveOverlay } from '@flipster/features/src/shared/responsive-overlay';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Dropdown } from '@flipster/react/design-system/dropdown';
import { inputRecipe } from '@flipster/react/design-system/input/input.recipe';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import SearchIcon from '@flipster/react/icon/generated/SearchIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { ListHeader } from './list-header';
import { ListItems } from './list-items';
import { RecentSearchHistory } from './recent-search-history';
import { marketListBunja, trackMarketListBunja } from './state';
import { TradFiCategoryChips } from './tradfi-category-chips';
import { type TabType } from './utils';

export const MarketListArea = () => {
  const [availableHeight, setAvailableHeight] = useState<number>();
  const { openSearchList } = useBunja(trackMarketListBunja);
  const { refreshRecentFavorites } = useBunja(marketListBunja);

  return (
    <ResponsiveOverlay.Root
      offset={10}
      dismissOnAncestorScroll
      onSizeApply={({ availableHeight }) => setAvailableHeight(availableHeight)}
      onOpen={() => {
        refreshRecentFavorites();
        openSearchList();
      }}
    >
      <CurrentMarketInfo />
      <MarketList availableHeight={availableHeight} />
    </ResponsiveOverlay.Root>
  );
};

const CurrentMarketInfo = () => {
  const { symbol } = useBunja(tradeSymbolBunja);
  const { instrumentAtomFamily } = useBunja(marketListBunja);
  const instrument = useAtomValue(instrumentAtomFamily(symbol));
  const { open } = useResponsiveOverlay();

  return (
    <ResponsiveOverlay.Trigger asChild>
      <button
        type='button'
        className={css(hstack.raw({ gap: 8 }), open && { '& > svg': { transform: 'rotate(-180deg)' } })}
        onClick={() => {
          segment.track(TradeEvent.TradeSearchClick, {
            trade_type: instrument.productType,
            symbol,
          });
        }}
      >
        <SymbolDisplay
          symbol={symbol}
          icon={<SymbolDisplay.Icon />}
          mainLabel={
            <div className={hstack({ gap: 4 })}>
              <SymbolDisplay.Label
                value='shortName'
                css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
              />
              <ProductTypeBadge productType={instrument.productType} />
            </div>
          }
          subLabel={
            instrument.isTradFi ? (
              <span className={css({ textStyle: 'Caption/L/Monospace/Regular', color: 'content.default.level-3' })}>
                {instrument.displayName}
              </span>
            ) : undefined
          }
        />
        <CaretDownIcon size={16} color={token('colors.content.default.level-2')} />
      </button>
    </ResponsiveOverlay.Trigger>
  );
};

export const MarketList = ({ availableHeight }: { availableHeight?: number }) => {
  return (
    <ResponsiveOverlay.Content
      dropdown={
        <Dropdown.Content css={{ w: 540 }} portal>
          <MarketListContent maxHeight={availableHeight} />
        </Dropdown.Content>
      }
      modal={<MarketListContent />}
    />
  );
};

const Tabs = createTabsComponent<TabType>();

const MarketListContent = ({ maxHeight }: { maxHeight?: number }) => {
  const t = useTranslations();
  const router = useRouter();

  const { isMobile } = useBreakpoint();
  const { currentTabAtom, searchAtom, tabsAtom, closestSymbolAtom } = useBunja(marketListBunja);
  const { clickTab, clickSymbol, clickSearchInput } = useBunja(trackMarketListBunja);
  const [search, setSearch] = useAtom(searchAtom);
  const [currentTab, setCurrentTab] = useAtom(currentTabAtom);
  const tabs = useAtomValue(tabsAtom);
  const closestSymbol = useAtomValue(closestSymbolAtom);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);
  const { setOpen } = useResponsiveOverlay();
  const inputRef = useRef<HTMLInputElement>(null);

  const tabsItems = useMemo(() => {
    const labels: Record<TabType, ReactNode> = {
      favorites: <FormattedMessage id='Trade.r260324.Favorites' />,
      perpetual: <FormattedMessage id='Assets.r250401.Perpetual' />,
      tradFi: <FormattedMessage id='Market.r260205.TradFi' />,
      spot: <FormattedMessage id='Assets.r250401.Spot' />,
    };

    return tabs.map((t) => ({ label: labels[t], value: t }));
  }, [tabs]);

  const inputCss = inputRecipe.raw();

  useEffectOnce(() => {
    if (!isMobile) {
      inputRef.current?.focus({ preventScroll: true });
      return;
    }

    const timeout = setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  });

  return (
    <div
      className={flex({ flexDir: 'column', overflow: 'hidden', bg: 'background.default.level-1', rounded: 12 })}
      style={isMobile ? { height: '80dvh' } : { height: maxHeight != null ? maxHeight - 70 : undefined }}
    >
      <div>
        <div className={css(inputCss.root, { m: 16, rounded: '1000px!' })}>
          <div className={css(inputCss.prefix)}>
            <SearchIcon size={20} />
          </div>
          <input
            ref={inputRef}
            name='market'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (!closestSymbol) return;
              router.push(closestSymbol);
              clickSymbol('Search list', closestSymbol, 0);
              setOrderAttribution(OrderAttributionMapper.trade[currentTab]);
              setOpen(false);
            }}
            onClick={clickSearchInput}
            className={css(inputCss.input)}
            placeholder={t('Autogen_common.r251126.s_Search_selections')}
          />
        </div>
      </div>
      {!search && <RecentSearchHistory />}
      <Tabs
        type='scrollable'
        size='small'
        border
        value={currentTab}
        onChange={(t) => {
          setCurrentTab(t);
          const index = tabsItems.findIndex((i) => i.value === t);
          clickTab(t, index);
        }}
        css={{ mb: 12 }}
      >
        {tabsItems.map(({ label, value }) => (
          <Tabs.Tab value={value} label={label} key={value} />
        ))}
      </Tabs>
      {currentTab === 'tradFi' && !search && <TradFiCategoryChips />}
      {!search && <ListHeader />}
      <ListItems />
    </div>
  );
};
